// cmsImageCredits — shared Alpine controller for the operator-editable
// per-website image photo-credit override. Two later surfaces reuse this:
// the legal-section credit list (Task 8, cms_image_credits.templ) and the
// CMS image-picker — both render the cmsCreditFields templ atom and wire its
// saveExpr to this controller's save(im) method, so the PATCH + dirty-signal
// logic lives in exactly one place.
//
// Config carries `creditBaseUrl`, server-rendered as
// "/organizations/{orgSlug}/websites/{websiteID}/api/legal" (Task 6's route
// group), and `images` — the per-image reactive state the caller seeds
// (id, url, author, license, source_url, license_url, title, has_override,
// and the UI-only `_hasLicense` toggle backing the license/source-url
// progressive-disclosure block).
//
// Search + selection (filtered/selected/select) mirror cmsImageCaptions
// (image-captions.ts) — the legal-section surface has the same left-list +
// right-preview shape, one reactive scope, no cross-component plumbing.
//
// Raw fetch (not HTMX), so CSRF must be attached explicitly: the token lives
// in the `<meta name="csrf-token">` tag, not a cookie — see getCSRFToken.
import { dispatchToast, getCSRFToken } from "../shared/htmx-helpers";
import { signalPublishStateChanged } from "../shared/publish-state-signal";

export interface CreditImage {
  id: string;
  url?: string;
  // media-04: set by the picker's editCredit so the takeover renders a
  // placeholder instead of a too-large image's raw url.
  processing_error?: string;
  width?: number;
  height?: number;
  author?: string;
  license?: string;
  source_url?: string;
  license_url?: string;
  title?: string;
  // Server-computed: true when the website has a credit override for this
  // image (website_image_credits row). Display-only "customized" indicator.
  has_override?: boolean;
  // Scope of the image, for the Bildnachweise scope filter: a property entry
  // has property_slug set; an area entry has area_slug set; a site-wide entry
  // has neither. (Only the credit-list surface seeds these — the picker leaves
  // them undefined, which resolves to the "sitewide" bucket, harmless there.)
  property_slug?: string;
  area_slug?: string;
  // UI-only: gates the license/source-url fields in cmsCreditFields. Not
  // sent to the server — save() derives the license field from it instead
  // (unchecked ⇒ license is cleared regardless of what's still in the
  // reactive field, so toggling off and saving actually clears it).
  _hasLicense?: boolean;
}

export interface CmsImageCreditsConfig {
  creditBaseUrl: string;
  images: CreditImage[];
  // Optional localized failure toast (server-rendered via t() by the
  // caller). Falls back to a generic string when not wired yet — mirrors
  // video-picker's `root?.dataset?.removeFailed || this.t_failed` pattern
  // for callers that haven't threaded a message through.
  saveFailedMsg?: string;
  // Localized label for the site-wide scope bucket in the scope filter
  // (property/area labels are derived from their slugs client-side, but
  // "site-wide" has no slug so its label is server-rendered via t()).
  scopeSitewideLabel?: string;
}

interface CreditScopeOption {
  key: string;
  label: string;
}

export function cmsImageCredits(config: CmsImageCreditsConfig) {
  return {
    creditBaseUrl: config.creditBaseUrl,
    images: (config.images || []) as CreditImage[],
    saveFailedMsg: config.saveFailedMsg,
    // Empty-string fallback (renders nothing) rather than a hardcoded English
    // literal — the mount (cmsImageCreditsXData) threads the localized label.
    scopeSitewideLabel: config.scopeSitewideLabel || "",
    q: "",
    // Selected scope keys ("sitewide" | "property:<slug>" | "area:<slug>").
    // Empty = no scope filter (show all).
    selectedScopes: [] as string[],
    scopeOpen: false,
    // Seed the preview with the first image so the pane is never empty on open.
    selectedId: (config.images && config.images[0]?.id) || "",

    // scopeKeyOf buckets an image: a property image, an area image, or
    // site-wide (neither slug). Mirrors imagePoolEntry's (property_slug,
    // area_slug) convention (website_deploy_assembly.go).
    scopeKeyOf(im: CreditImage): string {
      if (im.property_slug) return "property:" + im.property_slug;
      if (im.area_slug) return "area:" + im.area_slug;
      return "sitewide";
    },
    // scopeLabelOf prettifies a scope key for display: the server-provided
    // site-wide label, or the slug title-cased (properties/areas rarely have a
    // human name in the pool file, so the slug is the best cheap label).
    scopeLabelOf(key: string): string {
      if (key === "sitewide") return this.scopeSitewideLabel;
      const slug = key.slice(key.indexOf(":") + 1);
      return slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    // scopeOptions lists the distinct scopes actually present, ordered
    // site-wide → properties → areas, then alpha. Only worth showing the
    // filter when there's more than one bucket.
    scopeOptions(): CreditScopeOption[] {
      const keys = new Set<string>();
      for (const im of this.images) keys.add(this.scopeKeyOf(im));
      const rank = (k: string) => (k === "sitewide" ? 0 : k.startsWith("property:") ? 1 : 2);
      return [...keys]
        .map((key) => ({ key, label: this.scopeLabelOf(key) }))
        .sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label));
    },
    toggleScope(key: string) {
      const i = this.selectedScopes.indexOf(key);
      if (i === -1) this.selectedScopes.push(key);
      else this.selectedScopes.splice(i, 1);
    },
    clearScopes() {
      this.selectedScopes = [];
    },

    filtered(): CreditImage[] {
      const q = this.q.trim().toLowerCase();
      const scopes = this.selectedScopes;
      return this.images.filter((im) => {
        if (scopes.length && !scopes.includes(this.scopeKeyOf(im))) return false;
        if (!q) return true;
        return [im.author, im.title, im.license].some((s) => (s || "").toLowerCase().includes(q));
      });
    },
    selected(): CreditImage | null {
      return this.images.find((im) => im.id === this.selectedId) || null;
    },
    select(id: string) {
      this.selectedId = id;
    },

    // Plain-text approximation of the TASL line the deployed site renders
    // (studio-sites CreditsPage.astro): „title“ — author, license (source
    // url). A simplified live preview, not byte-identical to the Astro
    // render (no links) — good enough for the operator to sanity-check the
    // credit while editing.
    creditLine(im: CreditImage | null): string {
      if (!im) return "";
      const parts: string[] = [];
      if (im.title) parts.push(`„${im.title}“`);
      const bits: string[] = [];
      if (im.author) bits.push(im.author);
      if (im.license) bits.push(im.license);
      if (bits.length) parts.push(bits.join(", "));
      if (im.source_url) parts.push(im.source_url);
      return parts.join(" — ");
    },

    // Persist one image's credit override. Delegates to the shared
    // saveImageCredit() below — see its doc comment for the resp.ok gating /
    // dual-dispatch contract this surface and the CMS picker's saveCredit()
    // (image-picker.ts) both rely on.
    async save(im: CreditImage) {
      await saveImageCredit(config.creditBaseUrl, im, config.saveFailedMsg);
    },
  };
}

// saveImageCredit persists ONE image's credit override via PATCH
// {creditBaseUrl}/image-credits/{id}. Shared by cmsImageCredits.save (legal-
// section credit list, above) and the CMS image-picker's saveCredit()
// (image-picker.ts) — kept in exactly one place so the fetch shape, the
// resp.ok gating, and the dual dirty-signal dispatch never drift between the
// two surfaces.
//
// All-empty fields clear the override server-side (see updateImageCreditInput
// in handler_website_image_credits.go) — that's a valid save, not an error.
//
// The two dirty-signal dispatches must ONLY fire on a confirmed 2xx — firing
// them unconditionally would tell the publish button / CMS editor a save
// succeeded when a 403 (permission), CSRF rejection, or dropped connection
// actually left the credit unchanged. Mirrors the resp.ok gating in
// editor-autosave.ts (_flush/_flushCaptions) and the try/catch +
// dispatchToast fallback in editor-preview.ts's siblings (e.g.
// video-picker.remove / area-envelope-images._writeSlot).
//
// Returns true on a confirmed save, false on failure (already toasted) —
// callers that need to flip local state (e.g. the picker's pool `hasCredit`)
// check the return value instead of re-deriving success from a thrown error.
export async function saveImageCredit(
  creditBaseUrl: string,
  im: CreditImage,
  saveFailedMsg?: string,
): Promise<boolean> {
  const body = new URLSearchParams({
    author: im.author ?? "",
    license: im._hasLicense ? (im.license ?? "") : "",
    // source_url lives inside the SAME _hasLicense reveal block as license in
    // the cmsCreditFields atom (progressive disclosure) — it must be gated
    // identically, or unchecking "has license" + blanking author still sends
    // a non-empty source_url, which keeps the submission out of the
    // all-empty ClearCredit branch (handler_website_image_credits.go) and
    // silently re-saves a stale/orphaned source_url instead of clearing.
    source_url: im._hasLicense ? (im.source_url ?? "") : "",
    title: im.title ?? "",
  });
  try {
    const resp = await fetch(`${creditBaseUrl}/image-credits/${im.id}`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });
    if (!resp.ok) throw new Error(`save credit ${resp.status}`);
    // Two independent listeners need to know a credit changed:
    // - the management publish button (website-updated, bubbling from body)
    // - the CMS editor's own dirty/publish state when this surface is
    //   rendered INSIDE the editor (cms-external-dirty — editor.ts listens
    //   at module scope, see _setDirty bridge)
    document.body.dispatchEvent(new CustomEvent("website-updated", { bubbles: true }));
    window.dispatchEvent(new CustomEvent("cms-external-dirty"));
    // - the publish-state union (header + CMS Publish, build chip; plan 2 D11)
    signalPublishStateChanged();
    return true;
  } catch (err) {
    console.error("cms image credit save failed", err);
    // Empty-string fallback (renders nothing) rather than a hardcoded English
    // literal — both callers thread a localized message (cmsImageCreditsXData
    // and the picker's saveCredit via the dialog's data-credit-save-failed).
    dispatchToast(saveFailedMsg || "", "error");
    return false;
  }
}
